import { TOKEN, USER } from '@Constants/cookies';
import { loginRequest } from '@Services/auth/auth.req';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, LoginPayload } from './authSlice.types';
import { LoginResponse } from '@Services/auth/auth.res.types';

/* side effects */
export const login = createAsyncThunk<
	LoginResponse,
	LoginPayload,
	{ rejectValue: string }
>('auth/login', async ({ identifier, password }, thunkApi) => {
	try {
		const { data } = await loginRequest(identifier, password);
		return data;
	} catch (error: any) {
		const msg = error.response?.data.message || 'Login failed';
		return thunkApi.rejectWithValue(msg);
	}
});

/* slice */
/**
 * Auth must be restored synchronously at store creation: loginCheck()
 * is dispatched from an App effect that runs AFTER the first render
 * commit, by which point PrivateRoute has already seen isAuth=false and
 * redirected away — destroying deep links on every refresh.
 */
const readStoredAuth = (): Pick<AuthState, 'isAuth' | 'user' | 'grants'> => {
	const grants = new Map<string, string>();
	try {
		const isAuth = !!localStorage.getItem(TOKEN);
		const prevUser = localStorage.getItem(USER);
		const user = prevUser ? JSON.parse(prevUser) : null;
		if (user)
			for (let i = 0; i < user.grants?.length; i++)
				grants.set(user.grants[i].code, user.grants[i].name);
		return { isAuth, user, grants };
	} catch {
		return { isAuth: false, user: null, grants };
	}
};

const initialState: AuthState = {
	...readStoredAuth(),
	loaders: {
		login: null,
	},
	errors: {
		login: '',
	},
};
const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		loginCheck(state) {
			const restored = readStoredAuth();
			state.isAuth = restored.isAuth;
			state.user = restored.user;
			state.grants = restored.grants;
		},

		logout(state) {
			state.grants = new Map();
			state.isAuth = false;
			localStorage.clear();
			// Cookies.remove(TOKEN);
			// Cookies.remove(USER);
		},
	},
	extraReducers: (builder) => {
		/* login builder */
		builder
			.addCase(login.pending, (state) => {
				state.loaders.login = true;
				state.errors.login = '';
			})
			.addCase(login.fulfilled, (state, { payload }) => {
				localStorage.setItem(TOKEN, payload.data.token);
				localStorage.setItem(USER, JSON.stringify(payload.data.user));
				state.isAuth = true;
				state.user = payload.data.user;
				state.loaders.login = false;
				// Fresh map per login: the old code only ADDED, so grants from a
				// previous account survived a re-login without a reload.
				state.grants = new Map();
				for (let i = 0; i < state.user.grants.length; i++) {
					state.grants.set(
						state.user.grants[i].code,
						state.user.grants[i].name
					);
				}
			})
			.addCase(login.rejected, (state, action) => {
				state.errors.login = action.payload;
				state.loaders.login = undefined;
			});
	},
});

export default authSlice.reducer;
export const { loginCheck, logout } = authSlice.actions;
